import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect } from 'react';
import { LuEllipsisVertical, LuHourglass } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { TaskStatus, TaskView } from '@ndvr/customer-api';

import { useTableColumnSettings } from '@/cip/widgets/Table/useTableColumnSettings';
import { TableSettings } from '@/components/table/TableSettings';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDockState } from '@/views/dock/dock-state';
import { useHouseholdMap } from '@/views/household/household-queries';
import { formatDate, titleCase } from 'utils';
import { CustomTablePage, HouseholdLogoCard } from 'widgets';

import { useTaskOverlayState } from '../task-state';
import { exportTasksCsv, getTaskAssigneeLabel } from '../task-utils';
import { TaskStatusBadge } from './TaskBadge';
import { TaskFormType, toTaskFormValues } from './TaskForm';
import { TaskRecurrenceChip } from './TaskRecurrenceChip';
import { TaskTypeIcon } from './TaskTypeIcon';

const columnHelper = createColumnHelper<TaskView>();

const DEFAULT_COLUMN_VISIBILITY = {};

interface TaskListViewProps {
    tasks: TaskView[];
    isLoading?: boolean;
    exportCsvRef?: React.MutableRefObject<(() => void) | null>;
}

export function TaskListView({ tasks, isLoading, exportCsvRef }: TaskListViewProps) {
    const householdMap = useHouseholdMap();
    const { openViewTaskSheet: openView } = useTaskOverlayState();
    const navigate = useNavigate();
    const {
        columnVisibility,
        setColumnVisibility,
        columnSizing,
        setColumnSizing,
        columnOrder,
        setColumnOrder,
        resetSettings,
    } = useTableColumnSettings('tasks', { defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY });

    const exportCsv = useCallback(() => {
        exportTasksCsv(tasks, householdMap);
    }, [tasks, householdMap]);

    useEffect(() => {
        if (exportCsvRef) {
            exportCsvRef.current = exportCsv;
        }
    }, [exportCsv, exportCsvRef]);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor((row) => row.title || 'Untitled Task', {
                id: 'title',
                header: 'Title',
                cell: ({ row }) => (
                    <div className="flex max-w-[400px] items-center gap-2">
                        <span className="truncate">{row.original.title || 'Untitled Task'}</span>
                        <TaskRecurrenceChip frequency={row.original.frequency} />
                    </div>
                ),
                size: 280,
                minSize: 150,
            }),
            columnHelper.accessor(
                (row) =>
                    (row.householdGUID ? householdMap.get(row.householdGUID)?.globalAccountNamePlain : undefined) ||
                    'No household',
                {
                    id: 'household',
                    header: 'Household',
                    cell: ({ row }) => {
                        const task = row.original;
                        if (!task.householdGUID) {
                            return <span className="text-muted-foreground">No household</span>;
                        }
                        const name =
                            householdMap.get(task.householdGUID)?.globalAccountNamePlain || 'Unknown Household';
                        return (
                            <div onClick={(e) => e.stopPropagation()}>
                                <HouseholdLogoCard name={name} to={`/household/${task.householdGUID}`} />
                            </div>
                        );
                    },
                    size: 200,
                    minSize: 80,
                },
            ),
            columnHelper.accessor((row) => getTaskAssigneeLabel(row), {
                id: 'assignee',
                header: 'Assigned To',
                size: 180,
                minSize: 80,
            }),
            columnHelper.accessor('dueDate', {
                header: 'Due Date',
                cell: ({ row }) => {
                    const task = row.original;
                    return task.overdue && task.status !== TaskStatus.COMPLETED ? (
                        <div className="flex items-center gap-1.5" title="Overdue">
                            <LuHourglass className="h-3.5 w-3.5 shrink-0 text-red-500" />
                            <span>{formatDate(task.dueDate)}</span>
                        </div>
                    ) : (
                        <span>{formatDate(task.dueDate)}</span>
                    );
                },
                size: 140,
                minSize: 80,
            }),
            columnHelper.accessor('type', {
                header: 'Type',
                cell: ({ row }) => (
                    <div className="flex items-center space-x-1">
                        <TaskTypeIcon type={row.original.type} />
                        <span>{titleCase(row.original.type)}</span>
                    </div>
                ),
                size: 140,
                minSize: 80,
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: ({ row }) => (
                    <div className="max-w-[160px]">
                        <TaskStatusBadge status={row.original.status} />
                    </div>
                ),
                size: 160,
                minSize: 80,
            }),
            columnHelper.display({
                id: 'actions',
                header: ({ table }) => (
                    <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <TableSettings table={table} onReset={resetSettings} iconOnly />
                    </div>
                ),
                cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <TaskActions task={row.original} />
                    </div>
                ),
                size: 48,
                minSize: 48,
                meta: { isDisplayCol: true },
            }),
        ],
        [householdMap],
    );

    return (
        <CustomTablePage
            data={tasks}
            columns={columns}
            isLoading={isLoading}
            rowOnClick={(task) => {
                navigate(`/tasks/${task.taskGUID}`);
                openView(task.taskGUID);
            }}
            exportName="tasks"
            defaultSortCol="dueDate"
            defaultSortDirection="asc"
            cardClassName="m-0 border-0 shadow-none"
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnSizing={columnSizing}
            onColumnSizingChange={setColumnSizing}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
        />
    );
}

interface TaskActionsProps {
    task: TaskView;
}

const TaskActions = ({ task }: TaskActionsProps) => {
    const { openViewTaskSheet: openView, openDeleteTaskDialog: openDelete } = useTaskOverlayState();
    const { addUpdateTaskDockItem } = useDockState();
    const navigate = useNavigate();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
                <div className="hover:bg-ndvr-seafoam/50 hover:text-ndvr-dark inline-flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium">
                    <LuEllipsisVertical className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                    onClick={() => {
                        navigate(`/tasks/${task.taskGUID}`);
                        openView(task.taskGUID);
                    }}
                >
                    View
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => {
                        const formValues = toTaskFormValues(task) as TaskFormType;
                        addUpdateTaskDockItem(task.taskGUID, task.householdGUID ?? undefined, formValues, '', 'edit');
                    }}
                >
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => {
                        openDelete(task.taskGUID);
                    }}
                >
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
